import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { PostType, SnackbarSeverityType } from '../../enums';
import { FormData, NewSchool, School } from './types/school';
import { fetchSchools } from '../../redux/slices/School';
import { FormSectionTitle, StyledButton } from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import school from '../../apis/school';
import { isEmpty } from 'lodash';
import StyledCheckbox from '../common/styled/Checkbox';

const structuredPostTypes = [
  {
    name: PostType.INFIRMARY_UPDATE,
    label: 'Infirmary Update'
  },
  {
    name: PostType.LEAVE_APPLICATION,
    label: 'LEAVE_APPLICATION'
  },
  {
    name: PostType.AVAIL_LUNCH,
    label: 'Lunch Opt In / Opt Out'
  }
];

const AddSchoolForm = ({
  handleModalClose,
  setSnackbar
}: {
  handleModalClose: (setFormData: any) => void;
  setSnackbar: React.Dispatch<
    React.SetStateAction<{
      show: boolean;
      severity: SnackbarSeverityType;
      message: string;
    }>
  >;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { schools } = useSelector((state: RootState) => state.school);

  const initialFormData: FormData = {
    shortCode: '',
    displayCode: '',
    isSameAsShortCode: false,
    structuredPostTypes: [],
    cloneHashtags: false,
    cloneStructuredPost: false,
    cloneStructuredPostSchool: '',
    cloneHashtagSchool: ''
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formData.isSameAsShortCode) {
      setFormData((prev) => ({ ...prev, displayCode: prev.shortCode }));
    }
  }, [formData.shortCode, formData.isSameAsShortCode]);

  const handleShortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    // Allow only alphabets and hyphens
    const sanitizedValue = value.replace(/[^A-Z-]/g, '');
    setFormData((prev) => ({ ...prev, shortCode: sanitizedValue }));
  };

  const handleOnChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & {
          target: {
            value: unknown;
            name: string;
            checked?: boolean;
            type?: string;
          };
        })
  ) => {
    // Support both MUI and custom DropdownSelect event signatures
    const target = event.target as {
      name?: string;
      value?: unknown;
      checked?: boolean;
      type?: string;
    };
    const name = target.name;
    const value = target.value;
    const type = target.type;
    const checked = target.checked;

    setFormData((prev) => ({
      ...prev,
      [name as string]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStructurePostCheck = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      structuredPostTypes: checked
        ? [...prev.structuredPostTypes, name]
        : prev.structuredPostTypes.filter((item) => item !== name)
    }));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const newSchool: NewSchool = {
      shortCode: formData.shortCode,
      displayCode: formData.displayCode,
      hashtag: {
        cloneFrom: formData.cloneHashtagSchool
      },
      structuredPost: {
        cloneFrom: formData.cloneStructuredPostSchool,
        types: formData.structuredPostTypes
      }
    };

    try {
      const response = await school.saveSchool(newSchool);
      if (response) {
        setSnackbar({
          show: true,
          severity: SnackbarSeverityType.success,
          message: 'School added successfully!'
        });
        setLoading(false);
        handleCancel();
        dispatch(fetchSchools());
      }
    } catch (error: any) {
      setSnackbar({
        show: true,
        severity: SnackbarSeverityType.error,
        message:
          error.errors[0].message ?? 'Failed to add school. Please try again.'
      });
      setLoading(false);
      handleCancel();
    }
  };

  const handleCancel = () => {
    handleModalClose(setFormData(initialFormData));
  };

  return (
    <form
      onSubmit={(e) => {
        handleSave(e);
      }}
    >
      <Typography variant='h5' component='h2' style={{ fontWeight: 'bold' }}>
        Add New School
      </Typography>

      <TextField
        fullWidth
        margin='normal'
        label="School's Short Code"
        value={formData.shortCode}
        required
        name='shortCode'
        onChange={handleShortCodeChange}
        helperText='Only alphabets and hyphens are allowed.'
        variant='outlined'
      />

      <TextField
        fullWidth
        margin='normal'
        label="School's Display Code"
        value={formData.displayCode}
        required
        name='displayCode'
        onChange={(event) => {
          // Support both MUI and custom DropdownSelect event signatures
          const target = event.target as
            | HTMLInputElement
            | {
                name?: string;
                value?: unknown;
                checked?: boolean;
                type?: string;
              };
          const name = target.name;
          const value = target.value;
          setFormData((prev) => ({
            ...prev,
            [name as string]: value
          }));
        }}
        disabled={formData.isSameAsShortCode}
        variant='outlined'
      />
      <FormControlLabel
        control={
          <StyledCheckbox
            name='isSameAsShortCode'
            checked={formData.isSameAsShortCode}
            onChange={handleOnChange}
          />
        }
        label='Same as above'
      />

      <FormSectionTitle>Structured Posts</FormSectionTitle>
      <FormGroup>
        {structuredPostTypes.map((type) => (
          <FormControlLabel
            key={type.name}
            control={
              <StyledCheckbox
                name={type.name}
                checked={formData.structuredPostTypes.includes(type.name)}
                onChange={handleStructurePostCheck}
              />
            }
            label={type.label}
          />
        ))}
        {!isEmpty(formData.structuredPostTypes) && (
          <DropdownSelect
            label='School to clone from'
            placeholder='Select School'
            required
            name='cloneStructuredPostSchool'
            value={formData.cloneStructuredPostSchool}
            options={schools.map((school: School) => ({
              value: String(school.shortCode),
              label: school.shortCode
            }))}
            onChange={handleOnChange}
          />
        )}
      </FormGroup>

      <FormSectionTitle>Hashtags</FormSectionTitle>
      <FormControlLabel
        control={
          <StyledCheckbox
            name='cloneHashtags'
            checked={formData.cloneHashtags}
            onChange={handleOnChange}
          />
        }
        label='Clone hashtags from another school'
      />

      {formData.cloneHashtags && (
        <DropdownSelect
          label='School to clone from'
          placeholder='Select School'
          required
          name='cloneHashtagSchool'
          value={formData.cloneHashtagSchool}
          options={schools.map((school: School) => ({
            value: String(school.shortCode),
            label: school.shortCode
          }))}
          onChange={handleOnChange}
        />
      )}

      <Box
        style={{
          marginTop: 4,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 4
        }}
      >
        <StyledButton
          onClick={handleCancel}
          variant='contained'
          disabled={loading}
        >
          Cancel
        </StyledButton>
        <StyledButton variant='contained' type='submit' disabled={loading}>
          Save School
        </StyledButton>
      </Box>
    </form>
  );
};

export default AddSchoolForm;
